import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { navLinks, site, btnPrimary } from "../config/siteConfig";

const linkClass = ({ isActive }) =>
  `rounded-full px-3 py-2 text-[15px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
    isActive ? "text-brand font-semibold" : "text-slate-600 hover:text-brand"
  }`;

export const Header = () => {
  const [open, setOpen] = useState(false);
  const reduce = useReducedMotion();

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-brand-line bg-white/85 backdrop-blur-md"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
        <Link
          to="/"
          data-testid="header-logo-link"
          aria-label="Ansar Public School Mundeke — home"
          className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded-xl"
        >
          <Logo />
        </Link>

        <nav aria-label="Primary" className="hidden items-center gap-1 lg:flex">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={linkClass}
              data-testid={`desktop-nav-${l.label.toLowerCase()}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/admissions"
            data-testid="header-admission-cta"
            className={`${btnPrimary} hidden lg:inline-flex`}
          >
            Enquire About Admission
          </Link>
          <button
            type="button"
            data-testid="mobile-menu-button"
            aria-expanded={open}
            aria-controls="mobile-nav-drawer"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen(!open)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-brand-line text-brand transition-colors hover:bg-brand-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-nav-drawer"
            initial={reduce ? false : { opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden border-t border-brand-line bg-white lg:hidden"
          >
            <nav aria-label="Mobile" className="space-y-1 px-4 py-4">
              {navLinks.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={reduce ? false : { opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.04 * i }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block rounded-xl px-4 py-3 text-base font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
                        isActive
                          ? "bg-brand-light text-brand font-semibold"
                          : "text-slate-700 hover:bg-brand-cream"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="flex flex-col gap-2 pt-3">
                <Link
                  to="/admissions"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-admission-cta"
                  className={`${btnPrimary} w-full`}
                >
                  Enquire About Admission
                </Link>
                <a
                  href={site.phoneHref}
                  data-testid="mobile-call-link"
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-brand-line px-6 py-3 text-[15px] font-semibold text-brand transition-colors hover:bg-brand-light"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  {site.phoneDisplay}
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
